import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnchor, faMapMarkerAlt, faShip } from "@fortawesome/free-solid-svg-icons";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LogoLoader from "../components/LogoLoader";

// Import local images
import {
  HaldiaImg,
  DhamraImg,
  ParadeepImg,
  GopalpurImg,
  VisakhapatnamImg,
  GangavaramImg,
  KakinadaImg,
  KrishnapatnamImg
} from "../assets/data/Imagedata";

import seoData from "../assets/data/seo.json";

const Operations = () => {
  const [loading, setLoading] = useState(true);

  const { title, description, keywords, canonical, ogImage } = seoData.operations;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const portData = [
    {
      state: "West Bengal",
      ports: [
        {
          name: "Haldia Port",
          image: HaldiaImg,
          description: "A major sea port located at the meeting point of the Haldi River and Hooghly River."
        }
      ]
    },
    {
      state: "Odisha",
      ports: [
        {
          name: "Dhamra Port",
          image: DhamraImg,
          description: "One of the deepest ports in India, catering to bulk cargo."
        },
        {
          name: "Paradeep Port",
          image: ParadeepImg,
          description: "A major port on the East coast of India, handling significant dry bulk cargo."
        },
        {
          name: "Gopalpur Port",
          image: GopalpurImg,
          description: "Strategically located for easy access to the minerals of Odisha."
        }
      ]
    },
    {
      state: "Andhra Pradesh",
      ports: [
        {
          name: "Visakhapatnam Port",
          image: VisakhapatnamImg,
          description: "One of the largest ports in India."
        },
        {
          name: "Gangavaram Port",
          image: GangavaramImg,
          description: "The deepest port in India, with state-of-the-art infrastructure."
        },
        {
          name: "Kakinada Port",
          image: KakinadaImg,
          description: "A deep-water port known for its multi-cargo handling capabilities."
        },
        {
          name: "Krishnapatnam Port",
          image: KrishnapatnamImg,
          description: "A premier gateway on the East coast with world-class facilities."
        }
      ]
    }
  ];

  // Flattening port data for a single continuous slider
  const allPorts = portData.flatMap(region =>
    region.ports.map(port => ({ ...port, state: region.state }))
  );

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 8000,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    cssEase: "linear",
    pauseOnHover: true,
    arrows: false,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          speed: 5000,
        }
      }
    ]
  };

  if (loading) return <LogoLoader isLoading={loading} />;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[2s] scale-110"
          style={{
            backgroundImage: `url(${VisakhapatnamImg})`,
          }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <h1 className="text-[34px] font-TuskerGrotesk font-bold text-white mb-2 uppercase drop-shadow-2xl tracking-widest">
            OUR <span className="text-secondary italic">OPERATIONS</span>
          </h1>
          <div className="w-16 h-1 bg-secondary mx-auto rounded-full shadow-lg"></div>
          <p className="mt-4 text-[16px] text-white/90 font-medium tracking-[0.1em] uppercase max-w-2xl mx-auto drop-shadow-lg">
            Strategic Presence Across India's East Coast
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <div className="inline-flex items-center space-x-2 text-primary font-bold uppercase tracking-[0.2em] text-[12px] mb-4">
            <FontAwesomeIcon icon={faAnchor} className="animate-bounce" />
            <span>Extensive Network</span>
          </div>
          <h2 className="text-[34px] font-TuskerGrotesk text-black mb-4 uppercase italic tracking-widest">
            MAJOR STRATEGIC <span className="text-primary italic">GATEWAYS</span>
          </h2>
          <div className="w-16 h-1 bg-primary/20 mx-auto rounded-full mb-6" />
          <p className="text-slate-600 text-[14px] leading-relaxed font-medium">
            With decades of experience and deep-rooted expertise, Shivam Shipping & Logistics -SCM operates across the most vital maritime gateways of India. Our robust presence ensures seamless end-to-end logistics solutions for our global clientele.
          </p>
        </div>
      </section>

      {/* Ports Slider Listing */}
      <section className="py-16 overflow-hidden">
        <div className="px-4">
          <Slider {...sliderSettings} className="ports-slider -mx-4">
            {allPorts.map((port, idx) => (
              <div key={idx} className="px-4">
                <div className="group bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm transition-all duration-500 hover:shadow-2xl hover:border-primary/20 hover:-translate-y-3 overflow-hidden relative">
                  {/* State Badge */}
                  <div className="absolute top-8 right-8 z-20 bg-primary/90 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg transform transition-transform group-hover:scale-110">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1.5 text-[8px]" />
                    {port.state}
                  </div>

                  <div className="aspect-[4/3] mb-6 rounded-3xl overflow-hidden shadow-inner bg-slate-100 relative">
                    <img
                      src={port.image}
                      alt={port.name}
                      className="w-full h-full object-cover transform transition-transform duration-[2s] group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  </div>

                  <h4 className="text-[24px] font-bold text-black mb-3 group-hover:text-primary transition-colors uppercase tracking-wider">
                    {port.name}
                  </h4>
                  <p className="text-slate-500 leading-relaxed text-[12px] font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                    {port.description}
                  </p>

                  <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-all duration-700 transform translate-y-4 group-hover:translate-y-0">
                    <span className="text-primary font-bold text-[10px] uppercase tracking-[0.2em]">Explore Gateway</span>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all">
                      <FontAwesomeIcon icon={faShip} className="text-[10px]" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px] -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px] -ml-32 -mb-32" />

        <div className="container mx-auto px-4 text-center relative z-10 max-w-4xl">
          <h2 className="text-[34px] font-TuskerGrotesk mb-6 uppercase italic tracking-widest leading-tight text-white">READY TO MOVE <br /><span className="text-primary">YOUR CARGO?</span></h2>
          <p className="text-slate-400 text-[14px] mb-8 font-medium italic">
            Contact us today to leverage our extensive port network and professional vessel agency services.
          </p>
          <a
            href="/contactus"
            className="inline-flex items-center gap-2 bg-primary hover:bg-white hover:text-primary text-white font-bold py-4 px-10 rounded-xl transition-all duration-500 transform hover:-translate-y-1 shadow-2xl text-[16px]"
          >
            GET IN TOUCH <FontAwesomeIcon icon={faShip} className="ml-1" />
          </a>
        </div>
      </section>

      {/* Footer Decoration */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4 text-center">
          <div className="w-px h-16 bg-gradient-to-b from-primary to-transparent mx-auto mb-8"></div>
          <FontAwesomeIcon icon={faAnchor} className="text-primary/20 text-4xl mb-6 animate-pulse" />
          <h2 className="text-[18px] font-TuskerGrotesk text-slate-400 uppercase tracking-[0.5rem] mb-2 italic">EAST COAST OPERATIONS</h2>
          <p className="text-slate-400 text-[10px] font-bold tracking-[0.3rem] uppercase">Reliable Maritime Gateways</p>
        </div>
      </footer>
    </div>
  );
};

export default Operations;

